import { itemsAtom, optionGroupsAtom } from "@/atoms";
import { ItemOfferPrice } from "@/components/home";
import { InteractiveCard, Text } from "@/components/ui";
import { Image } from "@/components/image";
import { NoImage } from "@/components/no-image/NoImage";
import {
  RAIL_NAME_REM,
  RAIL_PAD_REM,
  railCardChrome,
  STOREFRONT_ROUNDED_PX,
  type StorefrontSizeRounded,
  type StorefrontSizeToken,
} from "@/domain/storefront-appearance";
import { calcMinGroupPrice } from "@/lib/calc-min-group-price";
import type { ItemOffer } from "@/types";
import { useAtomValue } from "jotai";
import { useMemo } from "react";

/**
 * Item card INSIDE A RAIL. Not `ItemOfferCard` (full-width list row). Shape
 * matches the reorder card: square photo left, text right, fixed height.
 */
export function RailItemCard({
  itemOffer,
  size,
  rounded,
  imageVisible,
  width,
  onClick,
}: {
  itemOffer: ItemOffer;
  size: StorefrontSizeToken;
  /** BOX corner. The photo inside keeps its own `rounded-md`. */
  rounded: StorefrontSizeRounded;
  imageVisible: boolean;
  /** Largura em px, vinda de `railCardWidth`. */
  width: number;
  onClick: (itemOfferId: string) => void;
}) {
  const items = useAtomValue(itemsAtom);
  const optionGroups = useAtomValue(optionGroupsAtom);

  const item = useMemo(
    () => items.find((candidate) => candidate.id === itemOffer.itemId),
    [items, itemOffer.itemId],
  );

  const inUseOptionGroups = useMemo(
    () =>
      optionGroups.filter(
        (group) => itemOffer.optionGroupsId?.includes(group.id),
      ),
    [optionGroups, itemOffer.optionGroupsId],
  );

  const minPrice = useMemo(
    () =>
      inUseOptionGroups.reduce(
        (total, group) => total + calcMinGroupPrice(group, "value"),
        0,
      ) + itemOffer.price.value,
    [inUseOptionGroups, itemOffer.price.value],
  );

  const minPriceIfDiscounted = useMemo(
    () =>
      inUseOptionGroups.reduce(
        (total, group) => total + calcMinGroupPrice(group, "originalValue"),
        0,
      ) + (itemOffer.price.originalValue ?? 0),
    [inUseOptionGroups, itemOffer.price.originalValue],
  );

  if (!item) return null;

  const chrome = railCardChrome(width, size);

  return (
    <InteractiveCard
      padding="none"
      onClick={() => onClick(itemOffer.id)}
      data-testid={`rail-item-card-${itemOffer.id}`}
      style={{
        width,
        height: chrome.heightPx,
        padding: `${RAIL_PAD_REM[size]}rem`,
        borderRadius: `${STOREFRONT_ROUNDED_PX[rounded]}px`,
      }}
      className="box-border flex shrink-0 items-stretch gap-2 overflow-hidden"
    >
      {imageVisible && (
        <div
          className="shrink-0 overflow-hidden rounded-card bg-gray-scale-100"
          style={{ width: chrome.photoPx, height: chrome.photoPx }}
        >
          {item.image?.URL ? (
            <Image
              width={200}
              height={200}
              alt={item.name}
              src={item.image.URL}
              crc={item.image["CRC-32"]}
              sizes="200px"
              fit="contain"
              className="h-full w-full"
            />
          ) : (
            <NoImage width={200} height={200} className="h-full w-full object-cover" />
          )}
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col justify-between">
        <Text clamp={2} weight="bold" leading="tight" style={{ fontSize: `${RAIL_NAME_REM[size]}rem` }}>
          {item.name}
        </Text>
        <ItemOfferPrice
          price={itemOffer.price}
          minPrice={minPrice}
          optionGroups={inUseOptionGroups}
          isOptionGroup={(itemOffer.optionGroupsId?.length ?? 0) > 0}
          minPriceIfDiscounted={minPriceIfDiscounted}
          size={size}
        />
      </div>
    </InteractiveCard>
  );
}
